// deps
import { useCallback, useContext } from "react";

// internal-deps
import {
    RenderingDetails,
    ReviewedCartApiResponse,
    SavingsLineItemData,
    SavingsRightSectionType,
    ApplyCouponsWidgetInfo,
    GiftingCartApiResponse,
    GiftingApplyCouponWidgetInfo,
} from "@swiggy-private/cart-types";

// contexts
import { CartFlowContext } from "@cart-client/contexts/CartFlowContext";

// helpers
import {
    canShowUnlockCouponComms,
    getAppliedCouponIcon,
    getAppliedCouponSubtitleText,
    getCombinedSavingsAndCountForGiftingCoupons,
    showCouponCodesListInGiftingCartSavingsCorner,
    getApplyCouponText,
    getApplyCouponTextForGiftingCart,
} from "@cart-client/helpers/Coupon";

// constants
import { APPLY_COUPON_TEXTS, CouponType } from "@cart-client/constants/Coupon";
import { CART_V2_APPLY_COUPON_ICON } from "@cart-client/constants/index";

// utils
import { format } from "@swiggy-private/js-utils";
import { isGiftingCartSource } from "@cart-client/helpers/GiftingCart";

// hooks
import { useCouponSavingsActions } from "@cart-client/hooks/useCouponSavingsActions";
import { useGiftingCouponCodeAnalytics } from "./useGiftingCouponCodeAnalytics";

export interface UseDirectCouponReturnType {
    getDirectCouponSavingsItem: (
        isCouponLockedWidgetVariant: boolean,
        renderingDetails: RenderingDetails,
        couponType?: CouponType,
    ) => SavingsLineItemData;
}

export const useDirectCouponSavingsData = (
    applyCouponWidgetData:
        | ReviewedCartApiResponse["apply_coupon_widget_info"]
        | GiftingCartApiResponse["apply_coupon_widget_info"],
    applyPaymentCouponWidgetData: ApplyCouponsWidgetInfo["payment_coupon"],
    onSavingsLineItemLoad: (title?: string, subtitle?: string, isPaymentCouponWidget?: boolean) => void,
    isNewPORFlow: boolean,
): UseDirectCouponReturnType => {
    const {
        onViewAllCouponsClick,
        onDirectApplyCouponCtaClick,
        onCouponLockedLineItemLoad,
        onDirectCouponLineItemLoad,
    } = useCouponSavingsActions({ applyCouponWidgetData, applyPaymentCouponWidgetData });
    const { data: cartData } = useContext(CartFlowContext);
    const { handleImpressionForGiftingCouponCode } = useGiftingCouponCodeAnalytics();
    const isGiftingCart = isGiftingCartSource(cartData?.cartSource);

    // coupon locked widget item data
    const getCouponLockedWidgetItemData = useCallback(
        (renderingDetails: RenderingDetails): SavingsLineItemData => {
            const title = {
                value: renderingDetails.display_text ?? APPLY_COUPON_TEXTS.APPLY_COUPON,
            };

            const subtitle = canShowUnlockCouponComms(applyCouponWidgetData)
                ? {
                      value: format(
                          APPLY_COUPON_TEXTS.UNLOCK_COUPON_WITH_X,
                          applyCouponWidgetData?.coupon_code,
                          applyCouponWidgetData?.amount_to_add,
                      ),
                  }
                : undefined;

            const rightSection = {
                type: SavingsRightSectionType.ICON,
            };

            return {
                icon: renderingDetails.icon || CART_V2_APPLY_COUPON_ICON,
                title,
                subtitle,
                rightSection,
                onLineItemClick: () => {
                    onViewAllCouponsClick(true);
                },
                onLoad: (...args) => {
                    onCouponLockedLineItemLoad(...args);
                    onSavingsLineItemLoad(title.value, subtitle?.value);
                },
                footerCommsStrip: applyCouponWidgetData?.potential_coupon_info,
            };
        },
        [applyCouponWidgetData, onCouponLockedLineItemLoad, onSavingsLineItemLoad, onViewAllCouponsClick],
    );

    const computeApplyCouponText = useCallback(
        (
            couponType: CouponType,
            applyCouponWidgetData: ReviewedCartApiResponse["apply_coupon_widget_info"],
            applyPaymentCouponWidgetData: ApplyCouponsWidgetInfo["payment_coupon"],
        ) => {
            if (isGiftingCart) {
                return getApplyCouponTextForGiftingCart(applyCouponWidgetData as GiftingApplyCouponWidgetInfo);
            }
            return getApplyCouponText(couponType, applyCouponWidgetData, applyPaymentCouponWidgetData);
        },
        [isGiftingCart],
    );

    const getApplyCTAText = useCallback(() => {
        if (isGiftingCart) {
            const { count } = getCombinedSavingsAndCountForGiftingCoupons(
                (applyCouponWidgetData as GiftingApplyCouponWidgetInfo)?.coupon_codes,
            );
            return count > 1 ? APPLY_COUPON_TEXTS.APPLY_ALL : APPLY_COUPON_TEXTS.APPLY;
        }
        return APPLY_COUPON_TEXTS.APPLY;
    }, [applyCouponWidgetData, isGiftingCart]);

    // Default direct coupon data
    const getDefaultDirectCouponItemData = useCallback(
        (icon: string, couponType: CouponType = CouponType.RESTAURANT): SavingsLineItemData => {
            const isPaymentCouponWidget = couponType === CouponType.PAYMENT;
            const title = {
                value: computeApplyCouponText(couponType, applyCouponWidgetData, applyPaymentCouponWidgetData),
            };

            const subtitle = {
                value: getAppliedCouponSubtitleText(couponType, isNewPORFlow),
                icon: true,
                onClick: () => {
                    onViewAllCouponsClick(false, isPaymentCouponWidget);
                },
            };

            const rightSection = {
                type: SavingsRightSectionType.BUTTON,
                value: getApplyCTAText(),
                onClick: () => onDirectApplyCouponCtaClick({ isPaymentCouponWidget }),
            };

            let couponCodeList;
            if (showCouponCodesListInGiftingCartSavingsCorner(applyCouponWidgetData)) {
                couponCodeList = {
                    applied: false,
                    codes: (applyCouponWidgetData as GiftingApplyCouponWidgetInfo)?.coupon_codes,
                    impressionEventCallback: handleImpressionForGiftingCouponCode,
                };
            }

            return {
                icon: icon || getAppliedCouponIcon(couponType),
                title,
                subtitle,
                rightSection,
                onLoad: (...args) => {
                    onDirectCouponLineItemLoad(...args, isPaymentCouponWidget);
                    onSavingsLineItemLoad(title.value, subtitle.value, isPaymentCouponWidget);
                },
                footerCommsStrip: !isPaymentCouponWidget ? applyCouponWidgetData?.potential_coupon_info : undefined,
                couponCodeList,
            };
        },
        [
            computeApplyCouponText,
            applyCouponWidgetData,
            applyPaymentCouponWidgetData,
            isNewPORFlow,
            getApplyCTAText,
            onViewAllCouponsClick,
            onDirectApplyCouponCtaClick,
            onDirectCouponLineItemLoad,
            onSavingsLineItemLoad,
            handleImpressionForGiftingCouponCode,
        ],
    );

    const getDirectCouponSavingsItem = useCallback(
        (isCouponLockedWidgetVariant: boolean, renderingDetails: RenderingDetails, couponType?: CouponType) => {
            if (isCouponLockedWidgetVariant) {
                return getCouponLockedWidgetItemData(renderingDetails);
            }
            return getDefaultDirectCouponItemData(renderingDetails.icon, couponType);
        },
        [getCouponLockedWidgetItemData, getDefaultDirectCouponItemData],
    );

    return { getDirectCouponSavingsItem };
};
